<template>
  <v-card class="pa-4" elevation="1">
    <!-- Una FILA por cavidad -->
    <div v-if="cavidadesResumen.length">
      <v-row
        v-for="cav in cavidadesResumen"
        :key="cav.id"
        class="mb-6 cav-row"
        align="stretch"
      >
        <!-- Solo nombre de la cavidad -->
        <v-col cols="12">
          <div class="subtitle-2 font-weight-medium mb-2">
            {{ cav.nombre }}
          </div>
        </v-col>

        <!-- Izquierda: gráfica de pastel (No OK por artículo) -->
        <v-col cols="12" md="4">
          <apexchart
            type="donut"
            height="260"
            :options="chartOpts(cav)"
            :series="cav.porArticulo.map(a => a.total)"
          />
        </v-col>

        <!-- Derecha: tabla de detalles -->
        <v-col cols="12" md="8">
          <div class="caption font-weight-medium mb-1">
            Detalle de No OK
          </div>
          <v-data-table
            :headers="headersDetalles"
            :items="cav.detalles"
            dense
            disable-pagination
            hide-default-footer
            class="no-ok-table"
          >
            <template v-slot:item.cantidad="{ item }">
              <div class="text-right font-weight-medium">
                {{ nf(item.cantidad) }}
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </div>

    <div v-else class="text-center caption my-6">
      Sin datos de piezas No OK.
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'PiezasNoOkTurno',
  props: {
    // JSON de horas: :horas="_rowsHora"
    horas: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    // Funciones helper (NO dentro de methods)
    nf() {
      return (n) => Number(n ?? 0).toLocaleString();
    },

    artLabel() {
      return (det) => {
        const id = det.idArticulo;
        const codigo = det.articuloCodigo || '';
        const nombre = det.articuloNombre || '';
        if (codigo && nombre) return `[${codigo}] ${nombre}`;
        if (nombre) return nombre;
        if (codigo) return codigo;
        return id != null ? `Artículo ${id}` : 'Artículo';
      };
    },

    chartOpts() {
      const nf = this.nf;

      return (cav) => {
        const labels = cav.porArticulo.map(a => a.label);
        const total = cav.totalNoOk || 0;

        return {
          labels,
          chart: {
            type: 'donut',
            toolbar: { show: false },
          },
          legend: {
            show: true,
            position: 'bottom',
            labels: { useSeriesColors: true },
          },
          tooltip: {
            y: {
              formatter: (value, opts) => {
                const idx = opts.seriesIndex;
                const abs = cav.porArticulo[idx]?.total || 0;
                if (!total) return `${nf(value)} pza(s)`;
                const pct = (abs / total) * 100;
                return `${nf(abs)} pza(s) (${pct.toFixed(1)}%)`;
              },
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (val, opts) => {
              const abs = opts.w.globals.series[opts.seriesIndex] || 0;
              if (!total) return '';
              const pct = (abs / total) * 100;
              return `${pct.toFixed(1)}%`;
            },
            style: { fontSize: '11px' },
          },
          stroke: { width: 2 },
          colors: ['#EF5350', '#FB8C00', '#8E24AA', '#26A69A', '#1E88E5', '#5E35B1'],
        };
      };
    },

    // Agrupa todos los noOkDetalles por cavidad (acepta idCavidad null)
    cavidadesResumen() {
      const horas = Array.isArray(this.horas) ? this.horas : [];
      const map = new Map(); // key -> cavidad

      horas.forEach(h => {
        const detalles = Array.isArray(h.noOkDetalles) ? h.noOkDetalles : [];

        detalles.forEach(d => {
          // Si no viene idCavidad, agrupamos por nombre (ej. "Única")
          const nombreCav = (d.cavidad || 'Única').trim();
          const key = (d.idCavidad != null)
            ? `id:${d.idCavidad}`
            : `nom:${nombreCav.toLowerCase()}`;

          const cantidad = Number(d.cantidad ?? 0) || 0;

          if (!map.has(key)) {
            map.set(key, {
              id: key,
              idCavidad: d.idCavidad ?? null,
              nombre: nombreCav,
              totalNoOk: 0,
              porArticulo: [], // [{ idArticulo, label, total }]
              detalles: [],     // [{ inicio, articulo, causa, subCausa, cantidad }]
            });
          }

          const cav = map.get(key);
          cav.totalNoOk += cantidad;

          const artId = d.idArticulo;
          const artLabel = this.artLabel(d);

          if (artId != null) {
            let art = cav.porArticulo.find(
              a => a.idArticulo === artId && a.label === artLabel
            );
            if (!art) {
              art = { idArticulo: artId, label: artLabel, total: 0 };
              cav.porArticulo.push(art);
            }
            art.total += cantidad;
          }

          cav.detalles.push({
            inicio: (d.inicio || '').replace('T', ' '),
            articulo: artLabel,
            causa: d.causa || '',
            subCausa: d.subCausa || '',
            cantidad,
          });
        });
      });

      return Array.from(map.values())
        .filter(c => c.totalNoOk > 0)
        .map(c => ({
          ...c,
          porArticulo: c.porArticulo.sort((a, b) => b.total - a.total),
        }))
        .sort((a, b) => b.totalNoOk - a.totalNoOk);
    },

    headersDetalles() {
      return [
        { text: 'Inicio',   value: 'inicio' },
        { text: 'Artículo', value: 'articulo' },
        { text: 'Causa',    value: 'causa' },
        { text: 'Subcausa', value: 'subCausa' },
        { text: 'Cantidad', value: 'cantidad', align: 'end' },
      ];
    },
  },
};
</script>

<style scoped>
.no-ok-table >>> .v-data-table__wrapper {
  max-height: 260px;
}

.cav-row + .cav-row {
  border-top: 1px solid #eee;
  padding-top: 12px;
}
</style>
